import type { FocusedElement } from '../state';
import type { OptionCallPut } from '../types';
import { useStableArray } from './useStableArray';
import { useOptionsSymbolSearchService } from './useSymbolSearchService';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { getSymbolSearchServiceQuery, selectPrices, selectExactMatches } from 'src/queries/symbolSearchService';

export function useValidPrices({
    underlyingSymbol,
    expirationDate,
    optionCallPut,
    onUpdate,
}: {
    underlyingSymbol?: string;
    expirationDate?: string;
    optionCallPut?: OptionCallPut;
    onUpdate: (element: FocusedElement, items: any[], loading?: boolean) => void;
}) {
    const { symbolSearchService } = useOptionsSymbolSearchService();

    const validPricesSelector = useMemo(
        () =>
            selectExactMatches({
                underlyingSymbol,
                expirationDate,
                optionCallPut,
                select: selectPrices,
            }),
        [underlyingSymbol, expirationDate, optionCallPut],
    );

    const { data: validPrices = [], isLoading: validPricesLoading } = useQuery({
        ...getSymbolSearchServiceQuery(underlyingSymbol ?? '', symbolSearchService),
        select: validPricesSelector,
    });

    // Stabilize validPrices array to prevent unnecessary re-renders
    const stableValidPrices = useStableArray(validPrices, (price) => String(price));

    useEffect(() => {
        if (validPricesLoading) {
            onUpdate('strikePrice', [], true);
        } else if (stableValidPrices.length > 0) {
            // Filter out invalid prices and get valid prices
            const sortedValidPrices = (stableValidPrices as number[])
                .filter((price: number): price is number => typeof price === 'number' && !isNaN(price))
                .toSorted((a, b) => a - b);

            onUpdate('strikePrice', sortedValidPrices, false);
        }
    }, [stableValidPrices, validPricesLoading, onUpdate]);
}
